import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { appUrl } from "../../../../../../utils/config";
import ReviewConsumptionDialog from "./ReviewConsumptionDialog";
import CustomDatePickerMui from "../../models/inputs/DateInput";

const useStyles = makeStyles(() => ({
  cancelButton: {
    fontSize: "1rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "#FF0000",
  },
  dialogTitle: {
    textAlign: "center",
    fontWeight: 600,
    fontSize: "1.6rem",
    marginTop: "8px",
    marginBottom: "20px",
  },
  warningIconLarge: {
    fontSize: "4rem",
    color: "#D32F2F",
    display: "block",
    margin: "0 auto 8px",
  },
  infoHeaderRow: {
    backgroundColor: "#EBF3FB",
    border: "1px solid #c5ddf0",
    borderRadius: "6px",
    padding: "12px 20px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  infoLabel: {
    fontWeight: 700,
    fontSize: "0.82rem",
    color: "#555",
    marginBottom: "2px",
  },
  infoValue: {
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  sectionHeader: {
    fontWeight: 700,
    fontSize: "0.95rem",
    marginTop: "20px",
    marginBottom: "8px",
  },
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #d0d0d0",
    borderRadius: "6px",
    marginBottom: "8px",
    overflowX: "hidden",
  },
  tableHeaderCell: {
    fontWeight: 700,
    backgroundColor: "#F5F5F5",
    fontSize: "0.72rem",
    padding: "8px 8px",
    whiteSpace: "normal",
    lineHeight: 1.3,
  },
  tableCell: {
    fontSize: "0.78rem",
    padding: "8px 8px",
  },
  warningText: {
    color: "#000",
    fontSize: "0.85rem",
    marginBottom: "8px",
  },
  warningInline: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#D32F2F",
    fontWeight: 600,
  },
  warningIconSmall: {
    fontSize: "1rem",
    color: "#D32F2F",
  },
  yesWarning: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#D32F2F",
    fontWeight: 700,
  },
  rejectButton: {
    backgroundColor: "#1565C0",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#0d47a1",
    },
  },
  approveButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 28px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
  errorText: {
    color: "#D32F2F",
    fontSize: "0.82rem",
    marginTop: "8px",
    textAlign: "center",
  },
  successDialog: {
    textAlign: "center",
    padding: "40px 20px",
  },
  successIcon: {
    fontSize: "80px",
    color: "#39811D",
    marginBottom: "20px",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
  },
  okButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
}));

// Format Date object → "YYYY-MM-DD" for <input type="date">
const toInputDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Round a numeric value to 3 decimal places for display
const toDecimal = (val) => {
  if (val === null || val === undefined || val === "-") return val;
  const num = Number(val);
  if (isNaN(num)) return val;
  return parseFloat(num.toFixed(3));
};

// Format any date string → display string
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
};

// Build consumed batch rows from fetchComponentAllocationPlannedAndActualNew response
const buildConsumedRows = (allocationData) => {
  if (!Array.isArray(allocationData)) return [];
  return allocationData.map((item) => {
    const planned = item.plannedLotNumberAllocationDetails || [];
    const actual = item.actualConsumptionDetails || [];

    // Build lot-level rows merging planned and actual
    const lotMap = {};
    planned.forEach((p) => {
      const key = p.lotNumber || "unknown";
      lotMap[key] = {
        batchNumber: p.lotNumber || "-",
        plannedAllocatedQty: p.plannedAllocatedQuantity || 0,
        actualConsumedQty: 0,
      };
    });
    actual.forEach((a) => {
      const key = a.componentBatchNumber || "unknown";
      if (lotMap[key]) {
        lotMap[key].actualConsumedQty = a.BomComponentConsumedQuantity || 0;
      } else {
        lotMap[key] = {
          batchNumber: a.componentBatchNumber || "-",
          plannedAllocatedQty: 0,
          actualConsumedQty: a.BomComponentConsumedQuantity || 0,
        };
      }
    });

    const lots = Object.values(lotMap);

    return {
      materialCode: item.componentCode || "-",
      materialName: item.componentName || "-",
      bomQuantity: item.componentQuantity != null ? item.componentQuantity : "-",
      uom: item.bomComponentRefUOM || item.uom || "-",
      lots,
      consumedQtyVariance: item.quantityVarianceValue,
      varianceWarning: item.quantityVariancePresent || false,
      batchesMismatch: item.lotsMismatch || false,
    };
  });
};

const VarianceMismatchDialog = ({ open, handleClose, row, onRefreshData }) => {
  const classes = useStyles();

  // Allocation data state
  const [allocationData, setAllocationData] = useState([]);
  const [consumedRows, setConsumedRows] = useState([]);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [qtyVarianceAcceptanceLimit, setQtyVarianceAcceptanceLimit] = useState(null);

  // Snapshot of planned allocated quantities at the time this dialog opened.
  // Keyed as "componentCode__lotNumber" → plannedAllocatedQuantity.
  // Passed to ReviewConsumptionDialog so it can display "Previous Planned Allocated Qty"
  // independently of its own fresh API call.
  const [plannedQtySnapshot, setPlannedQtySnapshot] = useState({});

  // Stat date state
  // presentDate is a stable reference for the current render cycle
  const presentDate = new Date();
  const sapPoStatDate = row?.statisticalDate || null;
  const acceptanceLimitEarly = row?.acceptanceLimitForEarlyDeliveryDateChangeByCMO ?? 15;
  const acceptanceLimitLate = row?.acceptanceLimitForLaterDeliveryDateChangeByCMO ?? 5;

  // Gray out the stat date field if the difference is within the acceptance limits
  const isStatDateFieldDisabled = React.useMemo(() => {
    if (!sapPoStatDate) return false;
    const stat = new Date(sapPoStatDate);
    if (isNaN(stat.getTime())) return false;
    const msPerDay = 1000 * 60 * 60 * 24;
    const earlyDiff = (stat.getTime() - presentDate.getTime()) / msPerDay; // Stat Date - Present Date
    const lateDiff = (presentDate.getTime() - stat.getTime()) / msPerDay;  // Present Date - Stat Date
    return (earlyDiff > 0 && earlyDiff < acceptanceLimitEarly) ||
           (lateDiff > 0 && lateDiff < acceptanceLimitLate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sapPoStatDate, acceptanceLimitEarly, acceptanceLimitLate]);

  const [updateStatDate, setUpdateStatDate] = useState("");

  // Approve loading/error/success state
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [approveSuccess, setApproveSuccess] = useState(false);

  // Review Consumption dialog state
  const [reviewConsumptionOpen, setReviewConsumptionOpen] = useState(false);
  const [approvedPOQty, setApprovedPOQty] = useState(null);

  // Fetch allocation data when dialog opens
  useEffect(() => {
    if (!open) return;

    // Reset state
    setApproveError("");
    setApproveSuccess(false);
    setReviewConsumptionOpen(false);
    setQtyVarianceAcceptanceLimit(null);
    // Auto-populate with present date only when field is not grayed out
    if (!isStatDateFieldDisabled) {
      setUpdateStatDate(new Date());
    } else {
      setUpdateStatDate(null);
    }

    const batchID = row?.schrockenDeliveryScheduleBatchID;
    const batchSize = row?.batchSize;

    if (!batchID || !batchSize) return;

    setLoadingAllocation(true);
    const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationPlannedAndActualNew/${batchID}/${batchSize}`;

    axios
      .get(url)
      .then((res) => {
        if (res.data.status) {
          const data = res.data.data || [];
          setAllocationData(data);
          setConsumedRows(buildConsumedRows(data));
          setQtyVarianceAcceptanceLimit(res.data.qtyVarianceAcceptanceLimit ?? null);

          // Build and store planned qty snapshot for ReviewConsumptionDialog memory
          const snapshot = {};
          data.forEach((item) => {
            const code = item.componentCode || "";
            (item.plannedLotNumberAllocationDetails || []).forEach((p) => {
              const key = `${code}__${p.lotNumber}`;
              snapshot[key] = p.plannedAllocatedQuantity || 0;
            });
          });
          setPlannedQtySnapshot(snapshot);
        } else {
          setAllocationData([]);
          setConsumedRows([]);
          setPlannedQtySnapshot({});
        }
      })
      .catch((err) => {
        console.error("Error fetching allocation data:", err);
        setAllocationData([]);
        setConsumedRows([]);
      })
      .finally(() => setLoadingAllocation(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleReject = () => {
    handleClose();
    if (onRefreshData) onRefreshData();
  };

  const handleApprove = async () => {
    setApproving(true);
    setApproveError("");

    const { statDateVariancePresent, producedQtyVariancePresent } = row || {};

    // Target status when approving variance/mismatch (same pattern as ApproveRejectConsumedBatch)
    const approvedStatusID = 39;
    const approvedStatusDescription = "Specs Approved";

    const payload = {
      BMSproductCode: row?.fgMaterialCode,
      BMSproductDesc: row?.fgMaterialName,
      scheduleAgreementNumber: row?.scheduleAgreementNumber,
      vendorBatchNumber: row?.vendorBatchNumber,
      vendorMosymphonyID: row?.vendorMosymphonyID,
      brandOwnerMosymphonyID: row?.brandOwnerMosymphonyID,
      batchDetailsList: [
        {
          statusID: approvedStatusID,
          statusDescription: approvedStatusDescription,
          schrockenDeliveryScheduleBatchID: row?.schrockenDeliveryScheduleBatchID,
        },
      ],
      statDateVariancePresent: !!statDateVariancePresent,
      producedQtyVariancePresent: !!producedQtyVariancePresent,
      scheduleLineDetails: {
       committedDeliveryDate:row?.committedDeliveryDate,
        schrockenSLID: row?.schrockenSLID,
        statisticalDate: row?.statisticalDate,
        targetQuantity: row?.targetQuantity,
        deliveryDate:row?.deliveryDate,
        committedQuantity:row?.committedQuantity===null?0:row?.committedQuantity,

       
      },
    };

    if (statDateVariancePresent && updateStatDate) {
      const d = updateStatDate.toDate ? updateStatDate.toDate() : new Date(updateStatDate);
      payload.updatedStatDate = d.toISOString();
    }

    if (producedQtyVariancePresent && row?.producedQuantity) {
      payload.updatedPOQty = row.producedQuantity;
    }

    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/approveVarianceMismatch`;
      const res = await axios.post(url, payload);
      if (res.data.success || res.data.status) {
        setApprovedPOQty(payload.updatedPOQty || row?.targetQuantity);
        setApproveSuccess(true);
      } else {
        setApproveError(res.data.message || "Approval failed. Please try again.");
      }
    } catch (err) {
      console.error("Error approving variance:", err);
      setApproveError(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setApproving(false);
    }
  };

  // Called when user clicks OK on success screen
  const handleOk = () => {
    const { producedQtyVariancePresent } = row || {};
    if (producedQtyVariancePresent && row?.consumptionSameAsAllocation) {
      setApproveSuccess(false);
      setReviewConsumptionOpen(true);
    } else {
      setApproveSuccess(false);
      handleClose();
      if (onRefreshData) onRefreshData();
    }
  };

  // Product Qty Variance row
  const productQtyRow = row
    ? {
        productCode: row.fgMaterialCode || "-",
        productName: row.fgMaterialName || "-",
        uom: row.batchUOM || "-",
        poQty: row.targetQuantity || 0,
        producedQty: row.producedQuantity || 0,
        variance:
          row.targetQuantity > 0
            ? Math.abs(
                (((row.producedQuantity || 0) - row.targetQuantity) /
                  row.targetQuantity) *
                  100
              ).toFixed(3)
            : "0.000",
      }
    : null;

  return (
    <>
      <Dialog
        open={open && !approveSuccess}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ style: { overflowX: "hidden" } }}
      >
        <DialogContent style={{ padding: "16px 20px 8px 20px", overflowX: "hidden" }}>
          <>
          {/* Cancel */}
          <Grid container justifyContent="flex-end" style={{ marginBottom: "8px" }}>
            <Typography
              variant="body2"
              className={classes.cancelButton}
              onClick={handleClose}
            >
              Cancel&nbsp;
              <CloseIcon fontSize="large" />
            </Typography>
          </Grid>

          {/* Icon + Title */}
          <Grid container justifyContent="center" direction="column" alignItems="center">
            <WarningIcon className={classes.warningIconLarge} />
            <Typography className={classes.dialogTitle}>
              Variance/Mismatch Alert
            </Typography>
          </Grid>

          {/* Header info */}
          <div className={classes.infoHeaderRow}>
            {[
              { label: "CMO", value: row?.vendorName || "-" },
              { label: "PO Number", value: row?.scheduleAgreementNumber || "-" },
              { label: "Batch Number", value: row?.vendorBatchNumber || "-" },
            ].map(({ label, value }) => (
              <div key={label}>
                <Typography className={classes.infoLabel}>{label}</Typography>
                <Typography className={classes.infoValue}>{value}</Typography>
              </div>
            ))}
          </div>

          {/* ── Section 1: Stat Date Variance ── */}
          <Typography className={classes.sectionHeader}>Stat Date Variance</Typography>
          <TableContainer
            component={Paper}
            className={classes.tableContainer}
            style={{ overflowX: "hidden" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "SAP PO Values - Statistical Date",
                    "Present Date",
                    "Acceptance Limit for (early) delivery date change by CMO (in days)",
                    "Acceptance Limit for (late) delivery date change by CMO (in days)",
                    "Update SAP PO Stat Date to *",
                  ].map((col) => (
                    <TableCell key={col} className={classes.tableHeaderCell}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.tableCell} style={{ whiteSpace: "nowrap" }}>
                    {formatDisplayDate(sapPoStatDate)}
                  </TableCell>
                  <TableCell className={classes.tableCell} style={{ whiteSpace: "nowrap" }}>
                    {formatDisplayDate(presentDate.toISOString())}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{acceptanceLimitEarly}</TableCell>
                  <TableCell className={classes.tableCell}>{acceptanceLimitLate}</TableCell>
                  <TableCell className={classes.tableCell}>
                    <CustomDatePickerMui
                      name="updateStatDate"
                      value={updateStatDate}
                      onChange={(momentObj) => setUpdateStatDate(momentObj)}
                      disabled={isStatDateFieldDisabled}
                      size="small"
                      notFullWidth
                      style={{
                        backgroundColor: isStatDateFieldDisabled ? "#f0f0f0" : "#fff",
                        borderRadius: 4,
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Section 2: Consumed Batch Variance/Mismatch ── */}
          <Typography className={classes.sectionHeader}>
            Consumed Batch Variance/Mismatch
          </Typography>
          {loadingAllocation ? (
            <Grid container justifyContent="center" style={{ padding: "20px" }}>
              <CircularProgress size={28} />
            </Grid>
          ) : (
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
              style={{ overflowX: "hidden" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "BMS Material Code / Description",
                      "BoM Quantity",
                      "UOM",
                      "Batch Numbers",
                      "Planned Allocated Qty",
                      "Actual Consumed Qty",
                      "Consumed Quantity Variance (%)",
                      "Batches Mismatch",
                    ].map((col) => (
                      <TableCell key={col} className={classes.tableHeaderCell}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consumedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" className={classes.tableCell}>
                        No consumption data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    consumedRows.map((material, mIdx) =>
                      material.lots.map((lot, lIdx) => (
                        <TableRow key={`${mIdx}-${lIdx}`}>
                          {lIdx === 0 && (
                            <TableCell
                              className={classes.tableCell}
                              rowSpan={material.lots.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              <div>{material.materialCode} /</div>
                              <div>{material.materialName}</div>
                            </TableCell>
                          )}
                          {lIdx === 0 && (
                            <TableCell
                              className={classes.tableCell}
                              rowSpan={material.lots.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              {toDecimal(material.bomQuantity)}
                            </TableCell>
                          )}
                          {lIdx === 0 && (
                            <TableCell
                              className={classes.tableCell}
                              rowSpan={material.lots.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              {material.uom}
                            </TableCell>
                          )}
                          <TableCell className={classes.tableCell}>{lot.batchNumber}</TableCell>
                          <TableCell className={classes.tableCell}>
                            {toDecimal(lot.plannedAllocatedQty)}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {toDecimal(lot.actualConsumedQty)}
                          </TableCell>
                          {lIdx === 0 && (
                            <TableCell
                              className={classes.tableCell}
                              rowSpan={material.lots.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              {(() => {
                                const variance = material.consumedQtyVariance;
                                if (variance === "NA" || variance === null || variance === undefined) {
                                  return <span>NA</span>;
                                }
                                if (variance === 0) {
                                  return "0";
                                }
                                const displayValue = variance % 1 !== 0 ? Number(variance).toFixed(3) : variance;
                                const isOverLimit = qtyVarianceAcceptanceLimit != null
                                  ? Math.abs(Number(variance)) > qtyVarianceAcceptanceLimit
                                  : true;
                                return (
                                  <span className={isOverLimit ? classes.warningInline : undefined}>
                                    {displayValue}
                                    {material.varianceWarning && (
                                      <WarningIcon className={classes.warningIconSmall} />
                                    )}
                                  </span>
                                );
                              })()}
                            </TableCell>
                          )}
                          {lIdx === 0 && (
                            <TableCell
                              className={classes.tableCell}
                              rowSpan={material.lots.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              {material.batchesMismatch ? (
                                <span className={classes.yesWarning}>
                                  YES&nbsp;
                                  <WarningIcon className={classes.warningIconSmall} />
                                </span>
                              ) : (
                                "NO"
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Section 3: Product Qty Variance ── */}
          <Typography className={classes.sectionHeader}>Product Qty Variance</Typography>
          {row?.producedQtyVariancePresent && (
            <Typography className={classes.warningText}>
              The produced quantity is outside the allowed variance limit of +/- 10%. Please
              confirm if you want to change the SAP PO qty to match the{" "}
              <span>produced qty.</span>
            </Typography>
          )}
          <TableContainer
            component={Paper}
            className={classes.tableContainer}
            style={{ overflowX: "hidden" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "BMS Product Code / Description",
                    "UOM",
                    "PO Qty",
                    "Produced Qty",
                    "Produced Quantity Variance (%)",
                  ].map((col) => (
                    <TableCell key={col} className={classes.tableHeaderCell}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {productQtyRow ? (
                  <TableRow>
                    <TableCell className={classes.tableCell}>
                      {productQtyRow.productCode} / {productQtyRow.productName}
                    </TableCell>
                    <TableCell className={classes.tableCell}>{productQtyRow.uom}</TableCell>
                    <TableCell className={classes.tableCell}>{toDecimal(productQtyRow.poQty)}</TableCell>
                    <TableCell className={classes.tableCell}>
                      {toDecimal(productQtyRow.producedQty)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {productQtyRow.variance}
                        {row?.producedQtyVariancePresent && (
                          <WarningIcon className={classes.warningIconSmall} />
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" className={classes.tableCell}>
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {approveError && (
            <Typography className={classes.errorText}>{approveError}</Typography>
          )}
          </>
        </DialogContent>

        <DialogActions
          style={{
            justifyContent: "center",
            gap: "24px",
            padding: "20px 36px 24px 36px",
          }}
        >
          <Button
            variant="contained"
            className={classes.rejectButton}
            onClick={handleReject}
            disabled={approving}
          >
            Reject Variance/Mismatch
          </Button>
          <Button
            variant="contained"
            className={classes.approveButton}
            onClick={handleApprove}
            disabled={approving}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {approving ? "Approving..." : "Approve Variance/Mismatch"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={approveSuccess}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleOk();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent className={classes.successDialog}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography className={classes.successTitle}>
            Variance/Mismatch Approved Successfully
          </Typography>
          <Button
            variant="contained"
            className={classes.okButton}
            onClick={handleOk}
            endIcon={<ChevronRightIcon />}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* Review Consumption Dialog — opened after successful approval when needed */}
      <ReviewConsumptionDialog
        open={reviewConsumptionOpen}
        handleClose={() => {
          setReviewConsumptionOpen(false);
          handleClose();
          if (onRefreshData) onRefreshData();
        }}
        row={row}
        plannedQtySnapshot={plannedQtySnapshot}
        originalPOQty={row?.targetQuantity}
        newPOQty={approvedPOQty}
        onRefreshData={onRefreshData}
      />
    </>
  );
};

export default VarianceMismatchDialog;
